import { matchPath } from 'react-router-dom';
import { RIGHTS } from './catalog';
import { Rule, always, anyOf, visible } from './levels';

// Source unique des droits par page : la garde de route ET le menu la lisent. Une page absente de la table est
// libre d'accès (accueil, profil, paramètres, rapports PowerBI dont la liste vient déjà de l'API).
// Règles reprises du menu BeOne (apps/common/templates/_primary_menu.php) : une entrée est visible si le droit vaut
// '0' ou '1', et chaque page vérifie le même droit à l'entrée (> 1 → erreur).
const PAGE_RULES: ReadonlyArray<{ pattern: string; rule: Rule }> = [
  // Marketing
  { pattern: '/marketing/calendar', rule: visible(RIGHTS.planning.calendar) },
  { pattern: '/marketing/new-campaign', rule: visible(RIGHTS.planning.createOperation) },
  // Une campagne existante se rouvre depuis la recherche, les brouillons ou le calendrier : n'importe lequel suffit
  {
    pattern: '/marketing/campaigns/:id',
    rule: anyOf(
      visible(RIGHTS.planning.createOperation),
      visible(RIGHTS.planning.advancedSearch),
      visible(RIGHTS.library.operation),
      visible(RIGHTS.library.draft),
      visible(RIGHTS.planning.calendar),
    ),
  },
  { pattern: '/marketing/preconizations', rule: visible(RIGHTS.preconization) },
  // La recherche avancée du POC remplace aussi la Bibliothèque de BeOne
  { pattern: '/marketing/advanced-search', rule: anyOf(visible(RIGHTS.planning.advancedSearch), visible(RIGHTS.library.operation)) },
  { pattern: '/marketing/drafts', rule: visible(RIGHTS.library.draft) },
  { pattern: '/marketing/recurring-offer', rule: visible(RIGHTS.library.offerRecurrence) },
  { pattern: '/marketing/customer-journey', rule: visible(RIGHTS.library.journey) },
  { pattern: '/marketing/timeslots', rule: visible(RIGHTS.library.timeslots) },

  // Clients : la fiche client s'ouvre depuis la recherche
  { pattern: '/clients/search', rule: visible(RIGHTS.customer.search) },
  { pattern: '/clients/dashboard/*', rule: visible(RIGHTS.customer.search) },
  { pattern: '/clients/dashboard', rule: visible(RIGHTS.customer.search) },
  { pattern: '/clients/new', rule: visible(RIGHTS.customer.create) },
  { pattern: '/clients/targetting-extract-files', rule: visible(RIGHTS.query.extract) },
  {
    pattern: '/clients/list-import',
    rule: anyOf(
      visible(RIGHTS.customer.cardList),
      visible(RIGHTS.customer.personIdList),
      visible(RIGHTS.customer.emailList),
      visible(RIGHTS.customer.phoneList),
      visible(RIGHTS.customer.walletList),
    ),
  },

  // Magasins : chaque droit store.* a son équivalent store_company.*
  { pattern: '/store/add-new-store', rule: visible(RIGHTS.store.create) },
  { pattern: '/store/show-store', rule: anyOf(visible(RIGHTS.store.show), visible(RIGHTS.store.companyShow)) },
  { pattern: '/store/update-store', rule: anyOf(visible(RIGHTS.store.modify), visible(RIGHTS.store.companyModify)) },

  // Acoustic
  { pattern: '/acoustic/*', rule: visible(RIGHTS.library.offers) },
];

// Pages qui n'existent que si l'enseigne a le module (réglage pgone, voir config/brandSettings.ts). Distinct des
// droits : sans le module, la page n'est pas « refusée », elle n'existe pas pour cette enseigne.
type BrandModuleKey = 'module.preconisation.active';

const PAGE_MODULES: ReadonlyArray<{ pattern: string; module: BrandModuleKey }> = [
  { pattern: '/marketing/preconizations', module: 'module.preconisation.active' },
];

export const pageModule = (pathname: string): BrandModuleKey | undefined =>
  PAGE_MODULES.find(({ pattern }) => matchPath(pattern, pathname))?.module;

export const pageRule = (pathname: string): Rule =>
  PAGE_RULES.find(({ pattern }) => matchPath(pattern, pathname))?.rule ?? always;
